import React, { useState } from 'react';

const WithdrawalAmountSelector = ({ 
  amount, 
  setAmount, 
  maxAmount, 
  minAmount, 
  currentBalance,
  onAmountChange 
}) => {
  const [customAmount, setCustomAmount] = useState('');

  // Preset amounts based on user's limits
  const getPresetAmounts = () => {
    const presets = [];
    
    // Add minimum amount
    if (minAmount && minAmount > 0) {
      presets.push(minAmount);
    }
    
    // Add common amounts
    const commonAmounts = [25, 50, 100, 250, 500];
    commonAmounts.forEach(amt => {
      if (amt <= maxAmount && amt >= minAmount) {
        presets.push(amt);
      }
    });
    
    // Add max amount if it's reasonable
    if (maxAmount <= 1000 && maxAmount > 500) {
      presets.push(maxAmount);
    }
    
    // Add current balance if it's within limits
    if (currentBalance && currentBalance <= maxAmount && currentBalance >= minAmount) {
      presets.push(currentBalance);
    }
    
    // Remove duplicates and sort
    return [...new Set(presets)].sort((a, b) => a - b);
  };

  const presetAmounts = getPresetAmounts();

  const handlePresetClick = (presetAmount) => {
    setAmount(presetAmount);
    setCustomAmount('');
    if (onAmountChange) {
      onAmountChange(presetAmount);
    }
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    console.log('Custom amount input value:', value);
    setCustomAmount(value);
    
    const numValue = parseFloat(value);
    console.log('Parsed number value:', numValue);
    
    // Always set the amount and call onAmountChange, let parent handle validation
    if (!isNaN(numValue)) {
      console.log('Setting amount to:', numValue);
      setAmount(numValue);
      if (onAmountChange) {
        console.log('Calling onAmountChange with:', numValue);
        onAmountChange(numValue);
      }
    }
  };

  const handleMaxClick = () => {
    const maxAllowed = Math.min(maxAmount, currentBalance);
    setAmount(maxAllowed);
    setCustomAmount(maxAllowed.toString());
    if (onAmountChange) {
      onAmountChange(maxAllowed);
    }
  };

  return (
    <div className="space-y-4">
      {/* Preset Amount Buttons */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
        {presetAmounts.map((presetAmount) => (
          <button
            key={presetAmount}
            type="button"
            onClick={() => handlePresetClick(presetAmount)}
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
              amount === presetAmount
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-gray-700 text-white border-gray-600 hover:bg-gray-600'
            }`}
          >
            ${Math.trunc(presetAmount * 100) / 100}
          </button>
        ))}
        
        {/* Max Amount Button */}
        {currentBalance && currentBalance > 0 && (
          <button
            type="button"
            onClick={handleMaxClick}
            className="px-3 py-2 rounded-lg border border-green-600 text-green-400 hover:bg-green-600 hover:text-white text-sm font-medium transition-colors"
          >
            MAX
          </button>
        )}
      </div>

      {/* Custom Amount Input */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Custom Amount
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            $
          </span>
          <input
            type="number"
            value={customAmount}
            onChange={handleCustomAmountChange}
            placeholder="Enter amount"
            min={minAmount}
            max={maxAmount}
            step="0.01"
            className="w-full pl-8 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="mt-2 text-xs text-gray-400">
          Min: ${minAmount} | Max: ${maxAmount} | Available: ${currentBalance}
        </div>
      </div>

      {/* Amount Validation */}
      {amount > 0 && (
        <div className={`p-3 rounded-lg text-sm ${
          amount > maxAmount 
            ? 'bg-red-900/20 border border-red-500/30 text-red-300'
            : amount < minAmount
            ? 'bg-yellow-900/20 border border-yellow-500/30 text-yellow-300'
            : 'bg-green-900/20 border border-green-500/30 text-green-300'
        }`}>
          {amount > maxAmount && (
            <p>⚠️ Amount exceeds maximum withdrawal limit of ${maxAmount}</p>
          )}
          {amount < minAmount && (
            <p>⚠️ Amount is below minimum withdrawal of ${minAmount}</p>
          )}
          {amount >= minAmount && amount <= maxAmount && (
            <p>✅ Amount is within withdrawal limits</p>
          )}
        </div>
      )}
    </div>
  );
};

export default WithdrawalAmountSelector; 